import {
  MarketingShell,
  Seo,
  Container,
  Section,
  PageHero,
  PostGrid,
  Card,
  Button,
  Reveal,
  Icon,
  CTASection,
} from './kit.jsx'

const POSTS = [
  {
    tag: 'Cold email',
    title: 'The 5-step sequence that gets prospects to reply',
    excerpt:
      'Intro, value, proof, nudge, break-up. We break down the exact cadence our top accounts run from their own Gmail — and why the 3-day wait between steps matters more than the copy.',
    author: 'Dana Ruiz',
    date: 'Jul 9, 2026',
    readTime: '7 min read',
  },
  {
    tag: 'Pipeline math',
    title: 'How many leads you actually need to close 10 deals',
    excerpt:
      'Reply rate, meeting rate, quote rate, win rate. Plug your real numbers into the funnel and you can back into how many of the 10,942 companies you should be working each week.',
    author: 'Marcus Bell',
    date: 'Jul 2, 2026',
    readTime: '6 min read',
  },
  {
    tag: 'Region strategy',
    title: 'Working a single region until it pays: a rep playbook',
    excerpt:
      'Spreading thin across every region kills momentum. Here is how to pick one territory, load every account in it, and run it to saturation before you expand.',
    author: 'Tariq Nolan',
    date: 'Jun 24, 2026',
    readTime: '8 min read',
  },
  {
    tag: 'Storm season',
    title: 'Your end-of-quarter outreach playbook for 2026',
    excerpt:
      'When budgets free up, the teams with a warm list win the deal. Build your sequences now, segment by territory, and be first in the inbox the morning the quarter turns.',
    author: 'Priya Anand',
    date: 'Jun 17, 2026',
    readTime: '9 min read',
  },
  {
    tag: 'Deliverability',
    title: 'Why sending from your own Gmail beats a blast tool',
    excerpt:
      'Shared IPs and spoofed domains land in spam. Sending sequences as yourself — real inbox, real reply thread — is the single biggest lever on whether prospects ever see your email.',
    author: 'Dana Ruiz',
    date: 'Jun 9, 2026',
    readTime: '5 min read',
  },
  {
    tag: 'Lead gen',
    title: 'Stop buying junk lead lists: qualify before you dial',
    excerpt:
      'Company size, established year, annual volume, service area. Four fields already in your workspace tell you which of the 10,942 companies are worth an hour of a rep’s day.',
    author: 'Marcus Bell',
    date: 'May 30, 2026',
    readTime: '6 min read',
  },
  {
    tag: 'Sales ops',
    title: 'Building a deal-flow pipeline your reps will actually update',
    excerpt:
      'A funnel only works if it reflects reality. We cover the stages that map to how contracting deals really move — new, contacted, quoted, won — and how to keep it clean.',
    author: 'Tariq Nolan',
    date: 'May 21, 2026',
    readTime: '7 min read',
  },
  {
    tag: 'Follow-up',
    title: 'The follow-up most reps skip (and why it closes)',
    excerpt:
      'Most reps quit after one email. The break-up message — sent 3 days after the last follow-up — pulls more replies than the intro. Here is the copy and the timing.',
    author: 'Priya Anand',
    date: 'May 12, 2026',
    readTime: '5 min read',
  },
  {
    tag: 'Analytics',
    title: 'Reading your reply rate: what 32% actually means',
    excerpt:
      'A high reply rate on a bad region is a vanity metric. Learn how to read Activity and Data views together so you invest in the territories and campaigns that produce real revenue.',
    author: 'Dana Ruiz',
    date: 'May 5, 2026',
    readTime: '6 min read',
  },
]

export default function BlogPage() {
  return (
    <MarketingShell active="">
      <Seo
        title="Blog"
        description="Field-tested playbooks on B2B sales, lead gen, cold email that actually lands, and pipeline math — from the team behind Revailo Sales."
        path="/blog"
        keywords="b2b sales blog, lead gen, cold email, sales pipeline, prospecting playbooks"
      />

      <PageHero
        eyebrow="Blog"
        title={<>Playbooks for teams that sell every day.</>}
        lead="Cold email that works for the trades, pipeline math you can act on, and region-by-region strategy — written by the people building Revailo Sales and the operators who use it."
      />

      <Section sm>
        <Container>
          <Reveal>
            <PostGrid posts={POSTS} />
          </Reveal>
        </Container>
      </Section>

      <Section sm>
        <Container>
          <Reveal>
            <Card soft>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 24,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, maxWidth: 620 }}>
                  <span
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      background: 'var(--mk-accent-tint)',
                      color: 'var(--mk-accent-dark)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: 'none',
                    }}
                  >
                    <Icon name="mail" size={22} />
                  </span>
                  <div>
                    <div className="m-0 font-semibold leading-[1.15] tracking-[-0.022em] text-ink" style={{ fontSize: 22 }}>
                      The B2B sales brief
                    </div>
                    <p className="m-0 mt-2 text-[14px] leading-[1.6] text-dim">
                      One field-tested play every other week — sequences, pipeline math, and region
                      strategy for B2B sales teams. No fluff, unsubscribe anytime.
                    </p>
                  </div>
                </div>
                <Button to="/signup" variant="accent" lg>
                  Get the newsletter
                </Button>
              </div>
            </Card>
          </Reveal>
        </Container>
      </Section>

      <CTASection
        title="The plays are better when the data is already loaded."
        body="Start free and put 10,942 verified companies behind every sequence you run."
        primary={{ label: 'Start free', to: '/signup' }}
        secondary={{ label: 'Book a demo', to: '/demo' }}
      />
    </MarketingShell>
  )
}
